/**
 * OutreachEncounterPanel — frontline outreach documentation for one consumer.
 *
 * Same composer UX as the Conversations tab (channel selector → template → compose
 * → send) with the added outreach functionality: engagement type + disposition
 * (configurable per program + MCP), timing/duration for billing, an AI SOAP
 * progress note, and billable-touch logging. Voice uses the full phone dialer
 * (MemberPhoneInterface) plus a Log-call option.
 */
import React, { useEffect, useState } from 'react';
import { Phone, MessageSquare, Mail, Loader2, Sparkles, Save, CheckCircle2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MemberPhoneInterface } from '@/components/communications/MemberPhoneInterface';
import outreachBillingApi, { OutreachEncounter, OutreachHistoryNote, TypeOption } from '@/services/api/outreachBilling';

const SOAP_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'subjective', label: 'Subjective' },
  { key: 'objective', label: 'Objective' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'plan', label: 'Plan' },
];

const FALLBACK_ENGAGEMENT: TypeOption[] = [
  { value: 'initial_outreach', label: 'Initial outreach' },
  { value: 'follow_up', label: 'Follow-up' },
];
const FALLBACK_DISPOSITION: TypeOption[] = [
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'declined', label: 'Declined' },
  { value: 'excluded', label: 'Excluded' },
];

type Channel = 'sms' | 'email' | 'voice';

interface Props {
  memberId: number;
  onChanged?: () => void;
}

export default function OutreachEncounterPanel({ memberId, onChanged }: Props) {
  const [enc, setEnc] = useState<OutreachEncounter | null>(null);
  const [smsTemplates, setSmsTemplates] = useState<Array<{ id: number; name: string; body: string }>>([]);
  const [emailTemplates, setEmailTemplates] = useState<
    Array<{ id: number; name: string; subject?: string | null; body: string }>
  >([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [channel, setChannel] = useState<Channel>('sms');
  const [engagementType, setEngagementType] = useState('initial_outreach');

  const [phone, setPhone] = useState('');
  const [emailAddr, setEmailAddr] = useState('');

  // SMS
  const [smsTemplateId, setSmsTemplateId] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsMinutes, setSmsMinutes] = useState('10');
  // Email
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailMinutes, setEmailMinutes] = useState('10');
  // Call log
  const [callMinutes, setCallMinutes] = useState('15');
  const [callNotes, setCallNotes] = useState('');

  const [soap, setSoap] = useState<Record<string, any>>({});
  const [savedNotes, setSavedNotes] = useState<OutreachHistoryNote[]>([]);

  const engagementTypes = enc?.engagement_types?.length ? enc.engagement_types : FALLBACK_ENGAGEMENT;
  const dispositionTypes = enc?.disposition_types?.length ? enc.disposition_types : FALLBACK_DISPOSITION;

  const load = async () => {
    setError(null);
    try {
      const e = await outreachBillingApi.getEncounter(memberId);
      setEnc(e);
      setPhone(e.phone || '');
      setEmailAddr(e.email || '');
      // The SOAP box is a fresh-note composer (blank), NOT a viewer of the last
      // note — hydrating it caused notes to appear to cross members and never
      // clear after save. Saved notes are shown as a list below instead.
      loadNotes();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load encounter');
    }
  };

  const loadNotes = async () => {
    try {
      const h = await outreachBillingApi.getMemberHistory(memberId);
      setSavedNotes(h.notes || []);
    } catch {
      setSavedNotes([]);
    }
  };

  useEffect(() => {
    // Clear per-member transient state before loading the next member so no
    // composer text, draft note, or status bleeds across member records.
    setSoap({});
    setSmsMessage('');
    setSmsTemplateId('');
    setEmailMessage('');
    setEmailSubject('');
    setEmailTemplateId('');
    setCallNotes('');
    setStatus(null);
    setError(null);
    setSavedNotes([]);
    load();
    outreachBillingApi.getTemplates('sms').then(setSmsTemplates).catch(() => {});
    outreachBillingApi.getTemplates('email').then(setEmailTemplates).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setStatus(null);
    try {
      await fn();
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const onSendSms = () =>
    run('sms', async () => {
      const r = await outreachBillingApi.sendSms(memberId, {
        template_id: smsTemplateId ? Number(smsTemplateId) : undefined,
        message: smsMessage || undefined,
        minutes: parseFloat(smsMinutes) || 10,
        to_number: phone || undefined,
        engagement_type: engagementType,
      });
      setStatus(r.sent ? 'SMS sent via Twilio.' : `SMS logged (Twilio: ${r.error || 'not sent'}).`);
      await load();
    });

  const onSendEmail = () =>
    run('email', async () => {
      const r = await outreachBillingApi.sendEmail(memberId, {
        template_id: emailTemplateId ? Number(emailTemplateId) : undefined,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
        minutes: parseFloat(emailMinutes) || 10,
        to_email: emailAddr || undefined,
        engagement_type: engagementType,
      });
      setStatus(r.sent ? 'Email sent.' : `Email logged (send: ${r.error || 'not sent'}).`);
      await load();
    });

  const onLogCall = () =>
    run('call', async () => {
      await outreachBillingApi.logCall(memberId, {
        minutes: parseFloat(callMinutes) || 0,
        notes: callNotes,
        to_number: phone || undefined,
        engagement_type: engagementType,
      });
      setStatus('Call logged.');
      setCallNotes('');
      await load();
    });

  const onDisposition = (value: string) =>
    run('disp', async () => {
      await outreachBillingApi.setDisposition(memberId, { disposition: value || null });
      await load();
    });

  const onDraft = () =>
    run('draft', async () => {
      const d = await outreachBillingApi.draftProgressNote(memberId);
      setSoap(d);
      setStatus(`AI draft ready (${d.source === 'ai' ? 'AI-generated' : 'demo fallback'}). Review & save.`);
    });

  const onSave = () =>
    run('save', async () => {
      await outreachBillingApi.saveProgressNote(memberId, soap, { channel, engagement_type: engagementType });
      // Clear the composer so the next note starts fresh, then refresh the
      // saved-notes list below.
      setSoap({});
      setStatus('Progress note saved to the member record.');
      await loadNotes();
    });

  if (!enc) {
    return <div className="p-4 text-sm text-gray-500">{error || 'Loading…'}</div>;
  }

  const isLaCare = /l\.?a\.? *care/i.test(enc.payer_name || '');
  const chanBtn = (c: Channel, label: string, Icon: any) => (
    <button
      onClick={() => setChannel(c)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
        channel === c
          ? 'bg-teal-600 text-white border-teal-600'
          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-50'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <div className="text-lg font-semibold">
            {enc.name} <span className="text-xs text-gray-400">({enc.member_number})</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={isLaCare ? 'bg-teal-100 text-teal-800' : 'bg-indigo-100 text-indigo-800'}>
              {enc.payer_name || 'No payer'}
            </Badge>
            {enc.prior_auth_number && <span className="text-xs text-gray-500">Prior auth {enc.prior_auth_number}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Engagement</label>
            <select
              className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
              value={engagementType}
              onChange={(e) => setEngagementType(e.target.value)}
            >
              {engagementTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Disposition</label>
            <select
              className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
              value={enc.disposition || ''}
              disabled={!!busy}
              onChange={(e) => onDisposition(e.target.value)}
            >
              <option value="">— none (in outreach) —</option>
              {dispositionTypes.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(status || error) && (
        <div
          className={`text-sm rounded-md px-3 py-2 ${
            error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
          }`}
        >
          {error || status}
        </div>
      )}

      {/* Contact */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-500 w-20">Phone</label>
        <input
          className="border rounded px-2 py-1 text-sm w-full sm:w-40 bg-white dark:bg-gray-800"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1…"
        />
        <label className="text-xs text-gray-500">Email</label>
        <input
          className="border rounded px-2 py-1 text-sm w-full sm:w-56 bg-white dark:bg-gray-800"
          value={emailAddr}
          onChange={(e) => setEmailAddr(e.target.value)}
          placeholder="member@example.com"
        />
      </div>

      {/* Channel selector — same UX as Conversations */}
      <div className="flex items-center gap-2">
        {chanBtn('sms', 'SMS', MessageSquare)}
        {chanBtn('email', 'Email', Mail)}
        {chanBtn('voice', 'Voice', Phone)}
      </div>

      {/* Composer per channel */}
      {channel === 'sms' && (
        <div className="border rounded-lg p-3 space-y-2">
          <select
            className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
            value={smsTemplateId}
            onChange={(e) => {
              setSmsTemplateId(e.target.value);
              const t = smsTemplates.find((x) => String(x.id) === e.target.value);
              if (t) setSmsMessage(t.body);
            }}
          >
            <option value="">— choose a template —</option>
            {smsTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <textarea
            className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
            rows={3}
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Message…"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Engagement min</label>
            <input
              type="number"
              min="1"
              className="border rounded px-2 py-1 text-sm w-20 bg-white dark:bg-gray-800"
              value={smsMinutes}
              onChange={(e) => setSmsMinutes(e.target.value)}
            />
            <Button size="sm" onClick={onSendSms} disabled={!!busy || !smsMessage}>
              {busy === 'sms' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />}{' '}
              Send SMS
            </Button>
          </div>
        </div>
      )}

      {channel === 'email' && (
        <div className="border rounded-lg p-3 space-y-2">
          <select
            className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
            value={emailTemplateId}
            onChange={(e) => {
              setEmailTemplateId(e.target.value);
              const t = emailTemplates.find((x) => String(x.id) === e.target.value);
              if (t) {
                setEmailMessage(t.body);
                if (t.subject) setEmailSubject(t.subject);
              }
            }}
          >
            <option value="">— choose a template —</option>
            {emailTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <input
            className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Subject"
          />
          <textarea
            className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
            rows={3}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            placeholder="Message…"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Engagement min</label>
            <input
              type="number"
              min="1"
              className="border rounded px-2 py-1 text-sm w-20 bg-white dark:bg-gray-800"
              value={emailMinutes}
              onChange={(e) => setEmailMinutes(e.target.value)}
            />
            <Button size="sm" onClick={onSendEmail} disabled={!!busy || !emailMessage}>
              {busy === 'email' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Send
              email
            </Button>
          </div>
        </div>
      )}

      {channel === 'voice' && (
        <div className="border rounded-lg p-3 space-y-3">
          <MemberPhoneInterface
            member={{ id: enc.member_id, phone_no: phone, first_name: enc.name, email: enc.email || undefined }}
            enrollmentId={enc.enrollment_id}
          />
          <div className="border-t pt-3 space-y-2">
            <div className="text-xs font-medium text-gray-500">Log this call (for tracking &amp; billing)</div>
            <textarea
              className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
              rows={2}
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              placeholder="Call notes / outcome…"
            />
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Minutes</label>
              <input
                type="number"
                min="1"
                className="border rounded px-2 py-1 text-sm w-20 bg-white dark:bg-gray-800"
                value={callMinutes}
                onChange={(e) => setCallMinutes(e.target.value)}
              />
              <Button size="sm" onClick={onLogCall} disabled={!!busy}>
                {busy === 'call' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Log
                call
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Touches */}
      <div>
        <div className="text-sm font-medium mb-1">Outreach touches ({enc.touches.length})</div>
        <div className="flex flex-wrap gap-2">
          {enc.touches.length === 0 && <span className="text-xs text-gray-400">None logged yet.</span>}
          {enc.touches.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-1"
              title={t.notes}
            >
              {t.channel === 'phone' ? (
                <Phone className="w-3 h-3" />
              ) : t.channel === 'email' ? (
                <Mail className="w-3 h-3" />
              ) : (
                <MessageSquare className="w-3 h-3" />
              )}
              {t.channel} · {t.minutes}m · POS {t.place_of_service || '02'} {t.date ? `· ${t.date}` : ''}
            </span>
          ))}
        </div>
      </div>

      {/* AI progress note */}
      <div className="border rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Progress note (SOAP)
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onDraft} disabled={!!busy}>
              {busy === 'draft' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}{' '}
              Draft with AI
            </Button>
            <Button size="sm" onClick={onSave} disabled={!!busy || !soap.assessment}>
              {busy === 'save' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
              note
            </Button>
          </div>
        </div>
        {SOAP_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs text-gray-500">{f.label}</label>
            <textarea
              className="border rounded px-2 py-1 text-sm w-full bg-white dark:bg-gray-800"
              rows={2}
              value={soap[f.key] || ''}
              onChange={(e) => setSoap({ ...soap, [f.key]: e.target.value })}
            />
          </div>
        ))}
      </div>

      {/* Saved progress notes (history for this member) */}
      <div className="border rounded-lg p-3">
        <div className="font-medium flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" /> Saved progress notes ({savedNotes.length})
        </div>
        {savedNotes.length === 0 ? (
          <div className="text-xs text-gray-400">No progress notes saved yet.</div>
        ) : (
          <div className="space-y-2">
            {savedNotes.map((n) => (
              <details key={n.id} className="rounded border bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
                <summary className="cursor-pointer text-sm flex items-center justify-between gap-2">
                  <span className="text-gray-700 dark:text-gray-200">
                    {n.date ? new Date(n.date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Saved note'}
                    {n.author ? ` · ${n.author}` : ''}
                  </span>
                  <span className="text-xs text-gray-500 truncate max-w-[50%]">{n.soap?.assessment || ''}</span>
                </summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {SOAP_FIELDS.map((f) => (
                    <div key={f.key}>
                      <div className="text-xs font-medium text-gray-500">{f.label}</div>
                      <div className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                        {n.soap?.[f.key] || <span className="text-gray-400">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
